import { notification } from "antd";
import {
  ArgsProps as NotificationProps,
  NotificationInstance,
} from "antd/lib/notification/interface";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import clsx from "clsx";
import React, { useContext, useMemo } from "react";

const iconMap = {
  info: <InfoCircleOutlined className="text-blue-500" />,
  success: <CheckCircleOutlined className="text-green-500" />,
  warning: <ExclamationCircleOutlined className="text-amber-500" />,
  error: <CloseCircleOutlined className="text-red-500" />,
};

const NotificationContext = React.createContext<NotificationType | undefined>(undefined);

export function useNotification(): NotificationType {
  const notificationApi = useContext(NotificationContext);
  if (notificationApi) {
    return notificationApi;
  } else {
    throw new Error("useNotification must be used within a NotificationProvider");
  }
}

export type { NotificationProps };

export function NotificationProvider({ children }: React.PropsWithChildren) {
  const [notificationApi, notificationContextHolder] = notification.useNotification({
    top: 86,
    duration: 10,
  });

  const customNotificationApi = useMemo<Notification>(
    () => new Notification(notificationApi),
    [notificationApi]
  );

  return (
    <NotificationContext.Provider value={customNotificationApi}>
      {notificationContextHolder}
      {children}
    </NotificationContext.Provider>
  );
}

export type NotificationType = InstanceType<typeof Notification>;

class Notification {
  private _notificationApi: NotificationInstance;

  constructor(notificationApi: NotificationInstance) {
    this._notificationApi = notificationApi;
  }

  open({ title, description, icon, type, ...args }: NotificationProps) {
    let actualIcon = icon;
    if (!icon) {
      actualIcon = type && iconMap[type];
    }
    if (actualIcon) {
      actualIcon = <div className="mt-5 ml-5">{actualIcon}</div>;
    }
    this._notificationApi.open({
      type,
      title: (
        <div className={clsx("p-6 -mr-6", { "-ml-12": actualIcon })}>
          <div className={clsx("text-sm mr-6", { "ml-12": actualIcon })}>{title}</div>
        </div>
      ),
      description: (
        <div className={clsx("text-sm", { "-ml-9": actualIcon }, { "p-6": description })}>
          {description}
        </div>
      ),
      icon: actualIcon,
      style: { padding: 0 },
      ...args,
    });
  }

  info(args: NotificationProps) {
    this.open({
      ...args,
      type: "info",
    });
  }

  success(args: NotificationProps) {
    this.open({
      ...args,
      type: "success",
    });
  }

  warning(args: NotificationProps) {
    this.open({
      ...args,
      type: "warning",
    });
  }

  error(args: NotificationProps, e?: Error) {
    this.open({
      ...args,
      type: "error",
    });
    if (e) {
      console.error(e);
    }
  }

  destroy(key: string) {
    this._notificationApi.destroy(key);
  }
}
